import type { ReactNode } from 'react';
import { Building2, ListChecks, MessageSquare, Phone, ShoppingBag, Sparkles, UserRound, Users } from 'lucide-react';
import { ClickToCall } from '@/components/common/ClickToCall';
import { useAbrirConversaInbox } from '@/hooks/useAbrirConversaInbox';
import { cn } from '@/lib/utils';
import type { Camada2, CardPronto, Contato, DadosPadrao, Empresa, Fonte, Pessoa } from '@/lib/dadosPadraoApi';
import { CAB_73A, CARD_73A, CHIP_73A, FILETE_73A, GHOST_73A, META_73A, SOLIDO_73A, TITULO_73A } from './LeadDados360Portabilidade';
import { cepFmt, cnpjFmt, cpfFmt, moeda, telefoneFmt } from './lead-cpf-blocos';

const SECAO = 'text-[10px] font-extrabold uppercase tracking-[1.4px] text-primary';
const PAR = 'flex min-h-[27px] items-center justify-between gap-3 border-t border-[#F0E9E1] px-3.5 text-[11px] dark:border-border';
const SUB = 'border-t border-[#F0E9E1] px-3.5 pt-2 text-[9px] font-extrabold uppercase tracking-[1.1px] text-primary dark:border-border';
const FONTE = 'inline-flex h-[18px] items-center rounded-[5px] border border-border px-[7px] text-[8.5px] font-bold tracking-[0.4px] text-muted-foreground';
const CARGO = 'inline-flex h-[18px] flex-shrink-0 items-center rounded-full border border-primary/40 px-2 text-[9.5px] font-bold tracking-[0.4px] text-primary';
const GRADE_CONTATOS = 'grid grid-cols-[200px_140px_100px_110px_minmax(0,1fr)_52px] items-center gap-x-3 px-3.5';
const VERDE = 'text-[#075E54] dark:text-[#25D366]';

const telefoneDaTela = (t: string) => telefoneFmt(t.startsWith('55') ? t.slice(2) : t);
const dataBr = (s: string | null) => {
  const d = s ? new Date(`${s.slice(0, 10)}T12:00:00`) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('pt-BR') : null;
};
const iniciais = (nome: string) => nome.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('');
const primeiroNome = (nome: string) => nome.split(/\s+/)[0]?.toLowerCase().replace(/^./, (c) => c.toUpperCase()) ?? nome;

export function Secao({ titulo, sub, children }: { titulo: string; sub?: string | null; children?: ReactNode }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className={SECAO}>{titulo}</span>
      {sub ? <span className="text-[11px] text-muted-foreground">{sub}</span> : null}
      <span className="h-px flex-1 bg-[#E2D7CA] dark:bg-border" />
      {children}
    </div>
  );
}

function Card({ titulo, meta, icone, children, className }: { titulo: string; meta?: string | null; icone: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn(CARD_73A, className)}>
      <header className={CAB_73A}>
        <span className={TITULO_73A}>{icone}{titulo}</span>
        {meta ? <span className={META_73A}>{meta}</span> : null}
      </header>
      {children}
    </section>
  );
}

function Par({ rotulo, children, primeiro }: { rotulo: string; children: ReactNode; primeiro?: boolean }) {
  return (
    <div className={cn(PAR, primeiro && 'border-t-0')}>
      <span className="text-muted-foreground">{rotulo}</span>
      <span className="min-w-0 truncate text-right text-[12px] font-bold tabular-nums">{children}</span>
    </div>
  );
}

function Fontes({ fontes }: { fontes: Fonte[] }) {
  return (
    <span className="flex gap-1">
      {fontes.map((f) => <span key={f} className={FONTE}>{f}</span>)}
    </span>
  );
}

function DotWhatsapp({ tem }: { tem: boolean | null }) {
  return tem
    ? <span className="h-[9px] w-[9px] flex-shrink-0 rounded-full bg-[#25D366] shadow-[0_0_0_3px_rgba(37,211,102,0.16)]" />
    : <span className="h-[9px] w-[9px] flex-shrink-0 rounded-full border-[1.5px] border-[#A6907B]" />;
}

export function CardDoTipo({ card }: { card: CardPronto }) {
  if (card.tipo === 'frase') {
    return (
      <Card titulo={card.titulo} icone={<Sparkles className="h-3 w-3" />}>
        <p className="px-3.5 py-3 text-[12px] leading-[1.55]">
          {card.partes.map((p, i) => (p.destaque ? <b key={i}>{p.texto}</b> : <span key={i}>{p.texto}</span>))}
        </p>
        {card.selos.length ? (
          <div className="flex flex-wrap gap-1.5 px-3.5 pb-3">
            {card.selos.map((s) => <span key={s} className={CHIP_73A}>{s}</span>)}
          </div>
        ) : null}
      </Card>
    );
  }
  if (card.tipo === 'numeros' || card.tipo === 'linhas') {
    return (
      <Card titulo={card.titulo} meta={card.tipo === 'numeros' ? card.meta : null} icone={card.tipo === 'numeros' ? <ShoppingBag className="h-3 w-3" /> : <ListChecks className="h-3 w-3" />}>
        {card.itens.map((i, k) => <Par key={i.rotulo} rotulo={i.rotulo} primeiro={k === 0}>{i.valor}</Par>)}
      </Card>
    );
  }
  if (card.tipo === 'selos') {
    return (
      <Card titulo={card.titulo} icone={<ListChecks className="h-3 w-3" />}>
        <div className="flex flex-wrap gap-1.5 px-3.5 py-3">
          {card.selos.map((s) => <span key={s} className={CHIP_73A}>{s}</span>)}
        </div>
      </Card>
    );
  }
  const grade = { gridTemplateColumns: `repeat(${card.colunas.length}, minmax(0, 1fr))` };
  return (
    <Card titulo={card.titulo} meta={`${card.linhas.length} ${card.linhas.length === 1 ? 'linha' : 'linhas'}`} icone={<ListChecks className="h-3 w-3" />} className="col-span-full">
      <div className="grid h-7 items-center gap-x-3 border-b border-[#F0E9E1] px-3.5 text-[8.5px] font-bold uppercase tracking-[1.1px] text-muted-foreground dark:border-border" style={grade}>
        {card.colunas.map((c) => <span key={c} className="truncate">{c}</span>)}
      </div>
      <div className="max-h-[220px] overflow-y-auto">
        {card.linhas.map((l, k) => (
          <div key={k} className={cn('grid h-9 items-center gap-x-3 border-b border-[#F0E9E1] px-3.5 text-[11.5px] last:border-b-0 dark:border-border', k % 2 === 1 && 'bg-[#FBF9F6] dark:bg-[#241A1A]')} style={grade}>
            {l.map((v, j) => <span key={j} className="truncate tabular-nums" title={v}>{v}</span>)}
          </div>
        ))}
      </div>
    </Card>
  );
}

function ContatoRealFaixa({ contato }: { contato: DadosPadrao['contatoReal'] }) {
  const algum = contato.nome || contato.telefone || contato.email;
  return (
    <div className={cn(CARD_73A, 'relative flex items-center gap-2.5 py-2.5 pl-[18px] pr-3.5')}>
      <span aria-hidden="true" className={FILETE_73A} />
      <UserRound className="h-3 w-3 text-muted-foreground" />
      <span className="text-[11px] font-bold uppercase tracking-[1.3px]">Contato real</span>
      {algum ? (
        <span className="flex min-w-0 items-center gap-3 text-[12px] font-semibold">
          {contato.nome ? <span className="truncate">{contato.nome}</span> : null}
          {contato.telefone ? <span className="tabular-nums">{telefoneDaTela(contato.telefone)}</span> : null}
          {contato.email ? <span className="truncate text-muted-foreground">{contato.email}</span> : null}
        </span>
      ) : (
        <span className="text-[11.5px] text-muted-foreground">nenhum confirmado</span>
      )}
    </div>
  );
}

function CardEmpresa({ empresa, cobertura, outras }: { empresa: Empresa | null; cobertura: Camada2['cobertura']; outras: Camada2['outrasEmpresas'] }) {
  if (!empresa) {
    return (
      <Card titulo="Empresa" meta="Receita Federal" icone={<Building2 className="h-3 w-3" />}>
        <div className="flex items-center gap-3 px-3.5 py-[18px]">
          <span className="h-[34px] w-[34px] flex-shrink-0 rounded-full border-[1.5px] border-[#A6907B]" />
          <span className="font-display text-[17px] font-bold">Este CPF não possui empresa</span>
        </div>
      </Card>
    );
  }
  const ativa = empresa.situacao === 'ATIVA';
  const porte = [empresa.mei ? 'MEI' : empresa.porte, empresa.simples === null ? null : empresa.simples ? 'Simples' : 'fora do Simples', empresa.mei === false ? 'não é MEI' : null].filter(Boolean).join(' · ');
  const e = empresa.endereco;
  const rua = e ? [[e.logradouro, e.numero].filter(Boolean).join(', '), e.bairro].filter(Boolean).join(' · ') : '';
  const cidade = e ? [[e.cidade, e.uf].filter(Boolean).join('/'), e.cep ? `CEP ${cepFmt(e.cep)}` : null].filter(Boolean).join(' · ') : '';
  return (
    <Card titulo="Empresa" meta="Receita Federal" icone={<Building2 className="h-3 w-3" />}>
      <div className="flex items-center gap-3 px-3.5 py-3">
        <span className={cn('flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-full border-[1.5px]', ativa ? 'border-[#075E54] dark:border-[#25D366]' : 'border-primary')}>
          <Building2 className={cn('h-4 w-4', ativa ? VERDE : 'text-primary')} />
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className={cn('font-display text-[22px] font-extrabold leading-none', ativa ? VERDE : 'text-primary')}>{empresa.situacao ?? '—'}</span>
          {dataBr(empresa.desde) ? <span className="text-[10.5px] text-muted-foreground">desde {dataBr(empresa.desde)}</span> : null}
        </div>
        <span className="ml-auto text-[11px] font-bold tabular-nums text-muted-foreground">{cnpjFmt(empresa.cnpj)}</span>
      </div>
      {empresa.razaoSocial ? <Par rotulo="Razão social">{empresa.razaoSocial}</Par> : null}
      {empresa.nomeFantasia ? <Par rotulo="Nome fantasia">{empresa.nomeFantasia}</Par> : null}
      {porte ? <Par rotulo="Porte">{porte}</Par> : null}
      {empresa.atividade ? <Par rotulo="Atividade">{empresa.atividade}</Par> : null}
      {empresa.capital !== null ? <Par rotulo="Capital social">{moeda(empresa.capital)}</Par> : null}
      {e ? (
        <>
          <div className={SUB}>Endereço e cobertura</div>
          <div className="px-3.5 pb-2 pt-1.5 text-[12px] font-semibold leading-[1.45]">
            {rua || '—'}
            {cidade ? <><br /><span className="text-[11px] font-normal text-muted-foreground">{cidade}</span></> : null}
          </div>
          <Par rotulo="Claro no CEP">
            {cobertura.claro
              ? <span className={cn('inline-flex items-center gap-1.5', VERDE)}><span className="h-2 w-2 rounded-full bg-[#075E54] dark:bg-[#25D366]" />{cobertura.claro.tipo ? `tem ${cobertura.claro.tipo.toLowerCase()}` : 'tem cobertura'}</span>
              : <span className="text-muted-foreground">sem cobertura no CEP</span>}
          </Par>
          <Par rotulo="Velocidade máxima">{cobertura.claro?.velocidadeMax ? `${cobertura.claro.velocidadeMax} Mega` : <span className="text-muted-foreground">—</span>}</Par>
        </>
      ) : null}
      {outras.length ? (
        <>
          <div className={SUB}>{outras.length === 1 ? 'Outra empresa do CPF' : 'Outras empresas do CPF'}</div>
          <div className="pb-1.5">
            {outras.map((o) => <Par key={o.cnpj} rotulo={cnpjFmt(o.cnpj)}>{[o.razaoSocial, o.situacao].filter(Boolean).join(' · ') || '—'}</Par>)}
          </div>
        </>
      ) : null}
    </Card>
  );
}

function LinhaDeContato({ contato, leadId, onLigar }: { contato: Contato; leadId: string; onLigar?: (t: string) => void }) {
  const { abrirConversa } = useAbrirConversaInbox();
  const telefone = contato.tipo === 'telefone' ? contato.valor.slice(2) : null;
  return (
    <div className="flex min-h-[30px] items-center gap-2 border-b border-[#F0E9E1] last:border-b-0 dark:border-border">
      <span className={cn('w-[150px] flex-shrink-0 truncate text-[13px] font-bold tabular-nums', !telefone && 'w-auto min-w-0 text-[12px]')} title={contato.valor}>
        {telefone ? telefoneDaTela(contato.valor) : contato.valor}
      </span>
      {telefone && contato.operadora ? <span className={CHIP_73A}>{contato.operadora}</span> : null}
      {telefone ? <DotWhatsapp tem={contato.whatsapp} /> : null}
      {telefone ? <span className="text-[10.5px] text-muted-foreground">{contato.whatsapp ? 'tem WhatsApp' : 'WhatsApp a conferir'}</span> : null}
      <span className="ml-auto flex items-center gap-1">
        <Fontes fontes={contato.fontes} />
        {telefone ? (
          <ClickToCall telefone={telefone} leadId={leadId} onClick={() => onLigar?.(telefone)}>
            <button type="button" className={GHOST_73A} title="Ligar pela discadora"><Phone className="h-[11px] w-[11px]" /></button>
          </ClickToCall>
        ) : null}
        {telefone && contato.whatsapp ? (
          <button type="button" className={SOLIDO_73A} title="Abrir WhatsApp" onClick={() => abrirConversa(telefone)}><MessageSquare className="h-[11px] w-[11px]" /></button>
        ) : null}
      </span>
    </div>
  );
}

function CardPessoas({ pessoas, contatos, ehCpf, leadId, onLigar }: { pessoas: Pessoa[]; contatos: Contato[]; ehCpf: boolean; leadId: string; onLigar?: (t: string) => void }) {
  const fontes = [...new Set(pessoas.flatMap((p) => p.fontes))];
  const meta = `${pessoas.length} ${pessoas.length === 1 ? 'pessoa' : 'pessoas'}${fontes.length ? ` · ${fontes.map((f) => f.charAt(0) + f.slice(1).toLowerCase()).join(' + ')}` : ''}`;
  return (
    <Card titulo={ehCpf ? 'Pessoa' : 'Pessoas da empresa'} meta={meta} icone={<Users className="h-3 w-3" />}>
      {pessoas.length === 0 ? (
        <p className="px-3.5 py-3 text-[12px] text-muted-foreground">A Receita não traz sócio para esta empresa.</p>
      ) : pessoas.map((p) => {
        const deles = contatos.filter((c) => (p.cpf && c.deQuem?.cpf === p.cpf) || (!p.cpf && c.deQuem?.nome === p.nome));
        const detalhe = [p.cpf ? `CPF ${cpfFmt(p.cpf)}` : p.cpfMascarado ? `CPF ${p.cpfMascarado}` : null, p.idade ? `${p.idade} anos` : p.faixaEtaria, p.cidade].filter(Boolean).join(' · ');
        return (
          <div key={p.cpf ?? p.nome} className="border-b border-[#F0E9E1] px-3.5 pb-1.5 pt-3 last:border-b-0 dark:border-border">
            <div className="flex items-center gap-3">
              <span className="flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-full bg-[linear-gradient(135deg,#7A2235,#4A1320)] font-display text-[14px] font-bold text-white">{iniciais(p.nome)}</span>
              <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
                <div className="flex min-w-0 items-center gap-[7px]">
                  <span className="truncate text-[13.5px] font-bold">{p.nome}</span>
                  {p.cargo ? <span className={CARGO}>{p.cargo.toUpperCase()}</span> : null}
                </div>
                {detalhe ? <span className="truncate text-[11px] tabular-nums text-muted-foreground">{detalhe}</span> : null}
              </div>
            </div>
            {deles.length ? (
              <div className="mt-2 pl-[54px]">
                {deles.map((c) => <LinhaDeContato key={c.valor} contato={c} leadId={leadId} onLigar={onLigar} />)}
              </div>
            ) : null}
          </div>
        );
      })}
    </Card>
  );
}

function CardContatos({ contatos, leadId, onLigar }: { contatos: Contato[]; leadId: string; onLigar?: (t: string) => void }) {
  const { abrirConversa } = useAbrirConversaInbox();
  const telefones = contatos.filter((c) => c.tipo === 'telefone').length;
  const emails = contatos.length - telefones;
  const meta = [`${telefones} ${telefones === 1 ? 'telefone' : 'telefones'}`, `${emails} ${emails === 1 ? 'e-mail' : 'e-mails'}`].join(' · ');
  return (
    <Card titulo="Telefones e e-mails" meta={meta} icone={<Phone className="h-3 w-3" />}>
      {contatos.length === 0 ? (
        <p className="px-3.5 py-3 text-[12px] text-muted-foreground">Nenhum telefone ou e-mail achado para este lead.</p>
      ) : (
        <>
          <div className={cn(GRADE_CONTATOS, 'h-7 border-b border-[#F0E9E1] text-[8.5px] font-bold uppercase tracking-[1.1px] text-muted-foreground dark:border-border')}>
            <span>Contato</span><span>De quem</span><span>Operadora</span><span>WhatsApp</span><span>Fonte</span><span />
          </div>
          {contatos.map((c, k) => {
            const telefone = c.tipo === 'telefone' ? c.valor.slice(2) : null;
            return (
              <div key={c.valor} className={cn(GRADE_CONTATOS, 'min-h-10 border-b border-[#F0E9E1] text-[11.5px] last:border-b-0 dark:border-border', k % 2 === 1 && 'bg-[#FBF9F6] dark:bg-[#241A1A]')}>
                <span className={cn('truncate font-bold tabular-nums', telefone ? 'text-[13px]' : 'text-[12px]')} title={c.valor}>{telefone ? telefoneDaTela(c.valor) : c.valor}</span>
                <span className="truncate" title={c.deQuem?.nome}>{c.deQuem ? primeiroNome(c.deQuem.nome) : 'empresa'}</span>
                <span>{telefone && c.operadora ? <span className={cn(CHIP_73A, 'inline-flex')}>{c.operadora}</span> : null}</span>
                <span className="inline-flex items-center gap-[7px] text-[11px] text-muted-foreground">{telefone ? <><DotWhatsapp tem={c.whatsapp} />{c.whatsapp ? 'tem' : 'a conferir'}</> : null}</span>
                <Fontes fontes={c.fontes} />
                <span className="flex justify-end gap-[5px]">
                  {telefone ? (
                    <ClickToCall telefone={telefone} leadId={leadId} onClick={() => onLigar?.(telefone)}>
                      <button type="button" className={GHOST_73A} title="Ligar pela discadora"><Phone className="h-[11px] w-[11px]" /></button>
                    </ClickToCall>
                  ) : null}
                  {telefone && c.whatsapp ? (
                    <button type="button" className={SOLIDO_73A} title="Abrir WhatsApp" onClick={() => abrirConversa(telefone)}><MessageSquare className="h-[11px] w-[11px]" /></button>
                  ) : null}
                </span>
              </div>
            );
          })}
        </>
      )}
    </Card>
  );
}

// A ABA DADOS EM DUAS CAMADAS (Douglas 04/11/2026, maquete v8 aprovada): em cima
// o que o lead trouxe, pelo modelo do tipo; embaixo o que sabemos, igual para
// todo lead, pelo CNPJ ou pelo CPF.
export function LeadDadosPadrao({ dados, leadId, completando, onLigar }: { dados: DadosPadrao; leadId: string; completando: boolean; onLigar?: (t: string) => void }) {
  const { camada1, camada2, contatoReal } = dados;
  const ehCpf = camada2?.documento.tipo === 'CPF';
  const fontes = camada2 ? [...new Set([...(camada2.empresa?.fontes ?? []), ...camada2.pessoas.flatMap((p) => p.fontes), ...camada2.contatos.flatMap((c) => c.fontes)])].filter((f) => f !== 'LEAD') : [];
  return (
    <div className="flex flex-col gap-3">
      {camada1 ? (
        <>
          <Secao titulo="O que o lead trouxe" sub={camada1.nome} />
          <div className="grid grid-cols-[repeat(auto-fit,minmax(290px,1fr))] gap-3">
            {camada1.cards.map((c) => <CardDoTipo key={c.titulo} card={c} />)}
          </div>
        </>
      ) : null}
      {camada2 ? (
        <>
          <div className="mt-2">
            <Secao titulo={ehCpf ? 'O que sabemos da pessoa' : 'O que sabemos da empresa'}>
              {fontes.map((f) => <span key={f} className={CHIP_73A}>{f}</span>)}
              {completando ? <span className={CHIP_73A}>completando</span> : null}
            </Secao>
          </div>
          <ContatoRealFaixa contato={contatoReal} />
          <div className="grid grid-cols-[420px_minmax(0,1fr)] items-start gap-3 max-[1100px]:grid-cols-1">
            <CardEmpresa empresa={camada2.empresa} cobertura={camada2.cobertura} outras={camada2.outrasEmpresas} />
            <CardPessoas pessoas={camada2.pessoas} contatos={camada2.contatos} ehCpf={ehCpf} leadId={leadId} onLigar={onLigar} />
          </div>
          <CardContatos contatos={camada2.contatos} leadId={leadId} onLigar={onLigar} />
        </>
      ) : null}
    </div>
  );
}
